import axios from "axios";

// Resolve backend URL with same-origin preference:
//   • In the browser, default to window.location.origin so API calls stay on
//     the user's own custom domain (e.g. https://prepmaster.ph). This avoids
//     cross-origin requests entirely and works around mobile carriers (PH
//     LTE, etc.) that filter unfamiliar TLDs like *.emergent.host.
//   • REACT_APP_BACKEND_URL is still honored as an override for SSR, tests,
//     and any deploy where the frontend and backend live on different hosts.
//   • Falls back to the env value during build-time/server-side rendering
//     when window is not available.
const ENV_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BROWSER_ORIGIN =
  typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : null;
// Native shells (Capacitor/Ionic) have a bogus origin like "capacitor://localhost"
// — falling back to the env URL for them is the only working option.
const IS_NATIVE_SHELL =
  BROWSER_ORIGIN &&
  (BROWSER_ORIGIN.startsWith("capacitor://") ||
    BROWSER_ORIGIN.startsWith("ionic://") ||
    BROWSER_ORIGIN === "http://localhost" ||
    BROWSER_ORIGIN === "https://localhost");
export const BACKEND_URL =
  BROWSER_ORIGIN && !IS_NATIVE_SHELL ? BROWSER_ORIGIN : ENV_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

/** Safely extract a human-readable error message from an axios error.
 *
 * FastAPI returns three different `detail` shapes:
 *   1. A plain string         → `"Current password is incorrect"`
 *   2. A list of validation errors (HTTP 422) → `[{type, loc, msg, input, ctx}, ...]`
 *   3. A nested object        → `{message: "..."}`
 *
 * Passing #2 or #3 straight into `toast.error()` causes React to crash with
 * "Objects are not valid as a React child" and unmounts the entire app
 * (blank screen). This helper normalises every shape to a single string.
 */
export function extractErrorMessage(err, fallback = "Something went wrong") {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    // FastAPI 422 validation list — join the human-friendly `msg` fields
    const msgs = detail
      .map((d) => (typeof d === "string" ? d : d?.msg))
      .filter(Boolean);
    if (msgs.length > 0) return msgs.join("; ");
  }
  if (detail && typeof detail === "object" && typeof detail.message === "string") {
    return detail.message;
  }
  if (typeof err?.message === "string" && err.message) return err.message;
  return fallback;
}

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("pm_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

/** Notify any UI layer (e.g. AuthContext) about a hard auth break. */
function broadcastAuthEvent(kind, payload = {}) {
  try { window.dispatchEvent(new CustomEvent("pm-auth-event", { detail: { kind, ...payload } })); } catch { /* noop */ }
}

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    // Handle admin-issued enrollment changes: server returns
    //   • 423 Locked with code='enrollment_suspended' (temporary)
    //   • 403 Forbidden with code='not_enrolled' (deleted — permanent)
    // Both share the "you no longer have access" UX so we broadcast one
    // event that the SuspendedAccessDialog listens for and renders.
    if (
      detail &&
      typeof detail === "object" &&
      (detail.code === "enrollment_suspended" || detail.code === "not_enrolled")
    ) {
      if (status === 423 || status === 403) {
        try {
          window.dispatchEvent(
            new CustomEvent("pm-enrollment-suspended", { detail })
          );
        } catch { /* noop */ }
      }
    }
    if (status === 401) {
      const replaced = detail === "session_replaced";
      // Only broadcast the hard auth break when the user was supposed to be authenticated.
      // (Calling /auth/login itself returns 401 on bad creds — that's expected, not a session break.)
      const url = err?.config?.url || "";
      const isLoginEndpoint = url.includes("/auth/login");
      if (!isLoginEndpoint) {
        if (replaced) {
          broadcastAuthEvent("session_replaced");
        } else {
          broadcastAuthEvent("logged_out");
        }
      }
    }
    // Deleted / deactivated / rejected students are rejected with HTTP 403 and
    // a detail string like "Account deleted" / "Account inactive" / "Account
    // rejected" (see backend/core.py get_current_user). This is a *hard* auth
    // break — the WebSocket `forced_logout` push is the fast path, but on
    // mobile Safari / PWA the WS is often suspended when the tab is
    // backgrounded, so the WS event never lands. The 30-second polling of
    // /auth/session/check will surface a 403 shortly after; catching it here
    // guarantees the client boots the user out even when the WS is dead.
    if (status === 403 && typeof detail === "string" && detail.startsWith("Account ")) {
      // Extract just the status word — "deleted" / "inactive" / "rejected"
      const status_word = detail.replace(/^Account\s+/, "").trim();
      broadcastAuthEvent("account_disabled", { reason: status_word });
    }
    return Promise.reject(err);
  }
);
